import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { Box, Grid, IconButton, Tooltip } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import PrintIcon from '@material-ui/icons/Print';
import HelpIcon from '@material-ui/icons/Help';
import ChecklistIcon from '../../../assets/img/planner-checklist-icon.svg';
import SeniorChecklist from '../../Checklist/Checklist';
import { useReactToPrint } from 'react-to-print';
import { isAdmin, isStudent, isAlumni } from '../../../util/miscUtil';

const Checklist = ({
  senior,
  alumni,
  roles,
  handleHelp,
  store,
  auth,
  handleAfterSaveReturn,
  setPage,
  handleReturnToSeniorList,
  classes,
  handleGotoAdminHomepage,
}) => {
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <summary className={classes.subPageContainer}>
      <div style={{ width: '100%', margin: 0 }} className={classes.checklistPageBanner}>
        <div className={classes.centerForm}>
          <Box
            sx={{
              width: '100%',
              maxWidth: {
                xs: '380px',
                sm: '540px',
                md: '868px',
                lg: '1320px',
                xl: '1320px',
              },
            }}
          >
            <Grid container spacing={0} direction="row" style={{ marginBottom: '-20px' }}>
              <Grid item xs={12} align="left">
                <summary className={classes.checklistHeader} style={{ paddingTop: '30px' }}>
                  <Grid container direction="row" className={classes.subPageHeaderWithIcons}>
                    <Grid item xs={false} sm={1} md={1} lg={1} align="center" className={classes.logoContainer}>
                      <img src={ChecklistIcon} alt="Checklist Icon" className={classes.logo} />
                    </Grid>
                    <Grid item xs={11} sm={11} md={8} lg={8} align="left">
                      <div className={classes.headerContent}>
                        <div className={classes.headerText}>
                          <div className={classes.title}>
                            {isStudent(roles) || isAlumni(roles) ? (
                              <main>Planning Checklist</main>
                            ) : (
                              <main>
                                Planning Checklist for {senior.alumni_updated_first_name ? senior.alumni_updated_first_name : senior.first_name}{' '}
                                {senior.alumni_updated_last_name ? senior.alumni_updated_last_name : senior.last_name}
                              </main>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        {isStudent(roles) ? (
                          <main className={classes.checklistIntroParagraph}>
                            This checklist will help you keep track of the most relevant activities to be completed during the year. In addition, it
                            will also help you get ready to embrace your chosen path after graduation. Each activity will earn you points and points
                            will be converted into a financial incentive. Work on this checklist with your Family Teachers and Successful Futures
                            Specialist and bring them to each of the Planning meetings. Please make sure you can show documentation that proves each
                            of the activities were completed.
                          </main>
                        ) : isAlumni(roles) ? (
                          <main className={classes.checklistIntroParagraph}>
                            This checklist was completed by the alumni during their senior year of high school to help them keep track of the most
                            relevant activities to be completed during the year. In addition, it helped them plan their chosen path after high school
                            graduation. You cannot modify this form.
                          </main>
                        ) : !alumni && isAdmin(roles) ? (
                          <main className={classes.checklistIntroParagraph}>
                            This checklist helps students keep track of the most relevant activities to be completed during their senior year. It also
                            helps them get ready to embrace their chosen paths after graduation. Each activity will earn them points and points will
                            be converted into financial incentives. They should work on this checklist with their Family Teachers and Successful
                            Futures Specialists and bring them to each of their Planning meetings. They should make sure they can show documentation
                            that proves each of the activities were completed.
                          </main>
                        ) : null}
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3} align="left">
                      <div className={classes.plannerIcons}>
                        <Tooltip title="Click to return to the planner home page." placement="bottom">
                          <IconButton color="inherit" aria-label="return home" onClick={(e) => setPage('home')}>
                            <HomeIcon className={classes.homeIcon} style={{ color: '#fff', fontSize: '1.20rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Click to return to open the printer dialog." placement="bottom">
                          <IconButton color="inherit" aria-label="print planning meeting 1 form" onClick={handlePrint}>
                            <PrintIcon className={classes.homeIcon} style={{ color: '#fff', fontSize: '1.20rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Click to return to view printer tips & tricks." placement="bottom">
                          <IconButton color="inherit" aria-label="printer tips and tricks" onClick={handleHelp}>
                            <HelpIcon className={classes.homeIcon} style={{ color: '#fff', fontSize: '1.20rem' }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>
                  </Grid>
                </summary>
              </Grid>
            </Grid>
          </Box>
        </div>
      </div>

      {!isStudent(roles) && !isAlumni(roles) && (
        <div className={classes.centerForm}>
          <Box
            sx={{
              width: '100%',
              maxWidth: {
                xs: '380px',
                sm: '540px',
                md: '868px',
                lg: '1280px',
                xl: '1280px',
              },
            }}
          >
            <Grid container direction="row" className={classes.unprintedContent}>
              <Grid item xs={12}>
                <div className={classes.breadcrumbs}>
                  <Breadcrumbs separator="›" aria-label="breadcrumbs" className={classes.alignCrumb}>
                    <span className={classes.breadCrumbLink} style={{ marginTop: '-2px' }} onClick={(e) => handleGotoAdminHomepage()}>
                      <HomeIcon style={{ fontSize: '1.30rem' }} />
                      <span style={{ margin: '1px 0 0 1px' }}>Home</span>
                    </span>
                    <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={(event) => handleReturnToSeniorList()}>
                      {alumni ? 'Alumni' : 'High School Seniors'}
                    </span>
                    <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={(e) => setPage('home')}>
                      {senior && senior.alumni_updated_first_name ? senior.alumni_updated_first_name : senior.first_name}{' '}
                      {senior && senior.alumni_updated_last_name ? senior.alumni_updated_last_name : senior.last_name}
                    </span>
                    <span className={classes.breadCrumbText} style={{ marginTop: '-1px' }}>
                      Planning Checklist
                    </span>
                  </Breadcrumbs>
                </div>
              </Grid>
            </Grid>
          </Box>
        </div>
      )}

      <div className={classes.centerForm} style={{ marginTop: '40px' }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: {
              xs: '380px',
              sm: '540px',
              md: '868px',
              lg: '1320px',
              xl: '1320px',
            },
          }}
        >
          <Grid container spacing={2} direction="row" className={classes.menu} ref={componentRef}>
            <Grid item xs={12} align="left" className={classes.printedContent}>
              <section className={classes.pageTitle}>
                <span className={classes.printHeaderText}>Senior Checklist</span>
              </section>
              <aside className={classes.printHeaderDesc}>
                {isStudent(roles) ? (
                  <main>
                    This checklist will help you keep track of the most relevant activities to be completed during the year. In addition, it will also
                    help you get ready to embrace your chosen path after graduation. Each activity will earn you points and points will be converted
                    into a financial incentive. Work on this checklist with your Family Teachers and Successful Futures Specialist and bring them to
                    each of the Planning meetings. Please make sure you can show documentation that proves each of the activities were completed.
                  </main>
                ) : alumni || isAlumni(roles) ? (
                  <main className={classes.headerDesc}>
                    This checklist was completed by the alumni during their senior year of high school to help them keep track of the most relevant
                    activities to be completed during the year. In addition, it helped them plan their chosen path after high school graduation. You
                    cannot modify this form.
                  </main>
                ) : !alumni && isAdmin(roles) ? (
                  <main className={classes.headerDesc}>
                    This checklist helps student keep track of the most relevant activities to be completed during their senior year. It also helps
                    them get ready to embrace their chosen paths after graduation. Each activity will earn them points and points will be converted
                    into financial incentives. They should work on this checklist with their Family Teachers and Successful Futures Specialists and
                    bring them to each of their Planning meetings. They should make sure they can show documentation that proves each of the
                    activities were completed.
                  </main>
                ) : null}
              </aside>
            </Grid>
            <Grid item xs={12}>
              <SeniorChecklist SeniorStore={store} AuthStore={auth} handleSaveReturn={handleAfterSaveReturn} />
            </Grid>
          </Grid>
        </Box>
      </div>
    </summary>
  );
};

Checklist.propTypes = {
  senior: PropTypes.object,
  alumni: PropTypes.bool,
  roles: PropTypes.array,
  handleHelp: PropTypes.func,
  store: PropTypes.object,
  auth: PropTypes.object,
  handleAfterSaveReturn: PropTypes.func,
  setPage: PropTypes.func,
  handleReturnToSeniorList: PropTypes.func,
  classes: PropTypes.object.isRequired,
  handleGotoAdminHomepage: PropTypes.func.isRequired,
};

export default Checklist;
